import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; // Import default Tippy.js styles
import styles from './tooltipnew.module.scss'; // Import custom SCSS styles
import { Instance } from 'tippy.js';
import { TooltipAnalyticsConfig } from '@msc-b2c/interfaces/types';
import {
  ReactElement,
  useEffect,
  useRef,
  useState,
  KeyboardEventHandler,
  FocusEventHandler,
  ReactNode,
} from 'react';
import { handleKeyDown, keyCodes } from '@msc-b2c/shared-utils/keyboardUtils';
import { getClickCtaMainName, trackEvent } from '@msc-b2c/shared-utils';

interface TooltipProps {
  children: ReactElement;
  content: ReactNode;
  appliedCssClassNames?: string;
  positionStyle: 'top' | 'bottom' | 'left' | 'right';
  zIndex?: number;
  showCloseButton?: boolean;
  closeIcon?: string;
  closeIconAriaLabel?: string;
  closeIconStyle?: string;
  tooltipIconRef?: React.RefObject<HTMLSpanElement>;
  triggerAriaLabel?: string;
  onTooltipShow?: () => void;
  onTooltipHide?: () => void;
  analytics?: TooltipAnalyticsConfig;
}

export const TooltipNew = ({
  children,
  content,
  appliedCssClassNames,
  positionStyle,
  zIndex,
  showCloseButton = false,
  closeIcon,
  closeIconAriaLabel,
  closeIconStyle,
  tooltipIconRef,
  triggerAriaLabel,
  onTooltipShow,
  onTooltipHide,
  analytics,
}: TooltipProps) => {
  const tooltipRef = useRef<any>(null);
  const triggerWrapperRef = useRef<HTMLDivElement>(null);
  const hasTrackedTooltipOpenRef = useRef(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const triggerAriaLabelText = triggerAriaLabel?.trim() || undefined;

  const trackTooltipAnalytics = () => {
    if (!analytics || hasTrackedTooltipOpenRef.current) {
      return;
    }

    const triggerElement = triggerWrapperRef.current;
    if (!triggerElement) {
      return;
    }

    const analyticsElement =
      triggerElement.querySelector<HTMLElement>(
        '[aria-label], .icon, .cmp-button__text, [title]'
      ) || triggerElement;

    trackEvent('click_cta', {
      cta_dest_link: analytics.ctaDestLink || '',
      click_main_name: getClickCtaMainName(analyticsElement),
      container_info: analytics.containerInfo || '',
      element_position: analytics.elementPosition || 'package card',
      element_type: analytics.elementType || 'tooltip',
      click_location: analytics.clickLocation || 'fixed',
    });

    hasTrackedTooltipOpenRef.current = true;
  };

  const handleOnShow = (instance: Instance) => {
    setIsVisible(true);
    trackTooltipAnalytics();
    onTooltipShow?.();
    if (instance.popper) {
      const tippyBox = instance.popper.querySelector('.tippy-box');
      if (tippyBox) {
        tippyBox.setAttribute('tabindex', '0');
        tippyBox.setAttribute('role', 'tooltip');
      }
    }
  };

  const blurTriggerFocus = () => {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && triggerRef.current?.contains(activeElement)) {
      activeElement.blur();
    }
  };

  const hideTooltip = () => {
    setIsVisible(false);
    tooltipRef.current?.hide();
    blurTriggerFocus();
  };

  const handleHidden = () => {
    setIsVisible(false);
    blurTriggerFocus();
  };

  const showTooltip = () => {
    setIsVisible(true);
    tooltipRef.current?.show();
  };

  const handleTriggerKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === keyCodes.ESCAPE) {
      if (tooltipRef?.current?.state.isVisible) {
        event.preventDefault();
        hideTooltip();
      }
      return;
    }

    if (!tooltipRef?.current?.state.isVisible) {
      handleKeyDown(event, showTooltip);
    }
  };

  const handleOnBlur: FocusEventHandler = (event) => {
    const relatedTarget = event.relatedTarget as Node | null;
    const popper = tooltipRef.current?.popper;

    if (relatedTarget && popper?.contains(relatedTarget)) {
      return;
    }

    hideTooltip();
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tooltipRef?.current) {
      tooltipRef.current.hide();
    }
  };

  const handleMouseEnter = () => {
    showTooltip();
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  useEffect(() => {
    const isToolTipVisible = () => tooltipRef?.current?.state.isVisible;

    const handleScroll = () => {
      if (isVisible || isToolTipVisible()) {
        hideTooltip();
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isVisible]);

  const tooltipContent = showCloseButton ? (
    <div
      className={styles['tooltipnew-content-wrapper']}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={closeIconAriaLabel}
        className={`${styles['tooltipnew-close-button']} ${closeIconStyle || ''} `}
        onClick={handleCloseClick}
      >
        <i className={`icon ${closeIcon || 'icon-close'}`}></i>
      </button>
      {content}
    </div>
  ) : (
    content
  );

  return (
    <Tippy
      reference={tooltipIconRef?.current}
      content={tooltipContent}
      className={`${styles['tooltipnew-container']} ${
        tooltipIconRef?.current ? styles['tooltipnew-container-styled'] : ''
      } ${appliedCssClassNames}`}
      visible={isVisible}
      placement={positionStyle}
      arrow={true}
      interactive={true}
      zIndex={zIndex}
      onShow={handleOnShow}
      onHide={() => {
        setIsVisible(false);
        hasTrackedTooltipOpenRef.current = false;
        onTooltipHide?.();
      }}
      onCreate={(instance) => (tooltipRef.current = instance)}
    >
      <div
        ref={triggerWrapperRef}
        role="button"
        tabIndex={0}
        aria-label={triggerAriaLabelText}
        aria-expanded={isVisible}
        onBlur={handleOnBlur}
        onFocus={showTooltip}
        onKeyDown={handleTriggerKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </Tippy>
  );
};
